import { useContext, useEffect, useMemo, useRef } from 'react';
import { GoogleAnalyticsContext } from '../../GoogleAnalytics';
import { Input } from '../../Input/Input';
import { useStepper } from '../../Stepper';
import eta from '../assets/eta.png';
import StepWrapper from './StepWrapper';

export const EXIT_DATE_MAX_DAYS = 45;

const startOfDay = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

export const getExitDateLimits = (referenceDate = new Date()) => {
  const today = startOfDay(referenceDate);
  const max = new Date(today);
  max.setDate(max.getDate() + EXIT_DATE_MAX_DAYS);
  return { today, max };
};

export const getExitDateValidation = (dateStr, referenceDate = new Date()) => {
  if (!dateStr) {
    return { isValid: false, error: '' };
  }

  const { today, max } = getExitDateLimits(referenceDate);
  const selected = startOfDay(new Date(dateStr));

  if (selected > max) {
    return { isValid: false, error: 'date of exit cannot exceed 45 days from today' };
  }

  if (selected < today) {
    return { isValid: false, error: 'Date must be in the future.' };
  }

  return { isValid: true, error: '' };
};

export function EtaExportPortPage() {
  const { localData, setDataByKey, activeStepKey, gotoStep } = useStepper();

  const exitDateValidation = useMemo(() => getExitDateValidation(localData.date), [localData.date]);

  const nextPage = () => {
    if (!exitDateValidation.isValid) {
      return;
    }

    if (localData.confirmedNoCG == 'NCG') {
      gotoStep('10. Commercial Documents');
    }
    if (localData.confirmedNoCG == 'CG') {
      if (
        localData.selectedCategoryOfGoods[0].category === 'Excise Goods - Wine, Beer, Spirits or Tobacco' &&
        !localData.selectedCategoryOfGoods[1]
      ) {
        gotoStep('10. Commercial Documents');
      } else {
        gotoStep('9. Documents');
      }
    }
  };

  const previousPage = () => {
    gotoStep('6. JobReference');
  };

  const isInitialized = useRef(false);
  const ga = useContext(GoogleAnalyticsContext);

  useEffect(() => {
    if (ga.gaInitialized) {
      if (activeStepKey === '8. EtaExportPort' && isInitialized.current === false) {
        isInitialized.current = true;
        window?.gtag?.('event', '8. EtaExportPort', {
          event_category: 'EPO',
        });
      }
    }
  }, [activeStepKey, ga.gaInitialized]);

  return (
    <StepWrapper
      stepTitle="Date of Exit"
      stepText="Date of Exit"
      stepKey="8. EtaExportPort"
      handleNextClick={nextPage}
      handleBackClick={previousPage}
      hideUnlessVisited
      disableNext={!exitDateValidation.isValid}
    >
      <div className="d-flex step-banner justify-content-center">
        <img src={eta} width="150" height="150" alt="port" className="mt-4 mb-4" />
      </div>

      <div className="d-flex align-self-center flex-column mt-auto px-4" style={{ maxWidth: '400px' }}>
        <span className="d-flex align-self-center flex-column" style={{ maxWidth: '280px', fontWeight: '500' }}>
          Select the Exit Date for this shipment.
        </span>
        <span className="d-flex align-self-center flex-column" style={{ maxWidth: '280px', fontWeight: '500' }}>
          Date changes can be made with the
        </span>

        <span className="d-flex align-self-center flex-column" style={{ maxWidth: '280px', fontWeight: '500' }}>
          calendar tool on the status screen.
        </span>
      </div>

      <div className="mt-auto" style={{ padding: '0 5rem' }}>
        <Input
          label="Date of Exit"
          name="dateOfExit"
          type="date"
          value={localData.date}
          required
          error={exitDateValidation.error}
          data-testid="shipmentExit-datePicker"
          onChange={(e) => setDataByKey('date', e.target.value)}
        />
      </div>
    </StepWrapper>
  );
}
